import { useState, useEffect, useRef, forwardRef } from 'react';
import { IconButton, Spinner } from '@components';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { router } from '@inertiajs/react';
import InternalDropdown from './internalDropdown';

let timeout;

export const ToolbarComponent = forwardRef(
  (
    { children, value, onChange, clear = () => {}, filtering = false, classes = '', placeholder },
    ref
  ) => {
    const icon = filtering ? (
      <Spinner size="sm" />
    ) : (
      <IconButton onClick={clear} icon={value !== '' ? XMarkIcon : MagnifyingGlassIcon} />
    );

    return (
      <>
        <div
          className={`mb-6 z-10 sticky top-4 toolbar h-[64px] flex w-full items-center ${classes}`}
        >
          <div className="bg-slate-50 flex items-center p-1.5 rounded-lg flex-shrink shadow h-[64px] rounded-lg xl:!rounded-r-none flex-shrink-0 w-full xl:w-auto">
            <InternalDropdown
              buttonProps={{
                className: 'xl:!rounded-lg !h-[54px] w-full xl:!w-[140px] !justify-center'
              }}
            />
          </div>
          <div
            className={`flex-1 shadow bg-white rounded-lg !rounded-l-none h-[64px] hidden xl:flex justify-between py-1 pr-6 flex-shrink-0 ${classes}`}
          >
            <div className="flex items-center w-full pr-6">
              <div className="ml-6 w-6 flex-shrink-0 flex items-center">{icon}</div>
              <input
                placeholder={placeholder}
                value={value}
                ref={ref}
                onChange={onChange}
                className="h-full flex-grow rounded-lg pl-4 text-lg focus:ring-0 border-0"
              />
            </div>
            <div className="inline-flex items-center">{children}</div>
          </div>
        </div>
      </>
    );
  }
);

export function Toolbar({
  children,
  filterUrl,
  autofocus = false
}: {
  children: ReactNode[];
  filterUrl: string;
  autofocus?: boolean;
}) {
  const currentUrl = new URL(filterUrl || window.location.href, window.location.origin);

  const getInitialKeyword = () => {
    return currentUrl.searchParams.get('keyword') || '';
  };

  const [keyword, setKeyword] = useState(getInitialKeyword());
  const [filtering, setFiltering] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autofocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autofocus]);

  const navigate = url => {
    router.visit(url.toString(), {
      medhot: 'get',
      onFinish: () => {
        setFiltering(false);
      }
    });
  };

  useEffect(() => {
    const currentUrlKeyword = currentUrl.searchParams.get('keyword') || '';

    if (keyword !== currentUrlKeyword) {
      clearTimeout(timeout);

      setFiltering(true);

      timeout = setTimeout(() => {
        if (keyword === '') {
          currentUrl.searchParams.delete('keyword');
        } else {
          currentUrl.searchParams.set('keyword', keyword);
        }

        router.visit(currentUrl.toString(), {
          medhot: 'get',
          onFinish: () => setFiltering(false)
        });
      }, 500);
    }
  }, [keyword]);

  return (
    <ToolbarComponent
      value={keyword}
      filtering={filtering}
      clear={() => setKeyword('')}
      onChange={() => setKeyword(e.target.value)}
    >
      {children}
    </ToolbarComponent>
  );
}
